/**
 * The long-lived environments and where each one is served — the one
 * machine-readable copy of the map in docs/environments.md.
 *
 * Read by the terminal dashboard (`tui/`), `scripts/get-auth-token.mjs`,
 * and the platform's Google OAuth redirect config. Adding an environment
 * here is what makes those tools see it. Keep this module free of imports
 * so a plain `node` script can load it through Node's type stripping.
 */

/**
 * Two data planes, not four isolated environments: `beta` shares
 * production's database and services, `dev` shares staging's.
 */
export type DataPlane = "production" | "staging";

/** The deployed apps: one public host and one Vercel project each, per environment. */
export const SERVICES = ["platform", "webapp", "media", "realtime"] as const;

export type ServiceName = (typeof SERVICES)[number];

/** Every Vercel project below lives in this team. */
export const VERCEL_TEAM_SLUG = "birdwatch-team";

/**
 * The short-link host of each data plane — the `BirdWatchOS/links` app
 * (`main` serves `bw.app`, `staging` serves `staging.bw.app`), which
 * redirects into its plane's webapp. For links that must stay short, such
 * as one in an SMS.
 */
export const SHORT_LINK_HOSTS: Readonly<Record<DataPlane, string>> = {
  production: "bw.app",
  staging: "staging.bw.app",
};

export interface Environment {
  /** The name used in `.environment/.env.<name>`, docs, and tool flags. */
  name: string;
  /** The git branch whose merges deploy it. */
  branch: string;
  plane: DataPlane;
  /**
   * Public host per service. `platform` serves the REST API and queue
   * callbacks (`https://<host>/api/v5`, `/queue/<jobKey>`), `webapp` the
   * single-page app, `media` uploads (`/api/v3/*`), `realtime` the relay.
   */
  hosts: Record<ServiceName, string>;
  /** Vercel project per service, in the `VERCEL_TEAM_SLUG` team. */
  vercelProjects: Record<ServiceName, string>;
}

export const ENVIRONMENTS = [
  {
    name: "dev",
    branch: "dev",
    plane: "staging",
    hosts: {
      platform: "dev.platform.birdwatch.com",
      webapp: "dev.app.birdwatch.com",
      media: "dev.media.birdwatch.com",
      realtime: "dev.realtime.birdwatch.com",
    },
    vercelProjects: {
      platform: "birdwatch-platform-dev",
      webapp: "birdwatch-webapp-dev",
      media: "birdwatch-media-dev",
      realtime: "birdwatch-realtime-dev",
    },
  },
  {
    name: "staging",
    branch: "staging",
    plane: "staging",
    hosts: {
      platform: "staging.platform.birdwatch.com",
      webapp: "staging.app.birdwatch.com",
      media: "staging.media.birdwatch.com",
      realtime: "staging.realtime.birdwatch.com",
    },
    vercelProjects: {
      platform: "birdwatch-platform-staging",
      webapp: "birdwatch-webapp-staging",
      media: "birdwatch-media-staging",
      realtime: "birdwatch-realtime-staging",
    },
  },
  {
    name: "beta",
    branch: "beta",
    plane: "production",
    hosts: {
      platform: "beta.platform.birdwatch.com",
      webapp: "beta.app.birdwatch.com",
      media: "beta.media.birdwatch.com",
      realtime: "beta.realtime.birdwatch.com",
    },
    vercelProjects: {
      platform: "birdwatch-platform-beta",
      webapp: "birdwatch-webapp-beta",
      media: "birdwatch-media-beta",
      realtime: "birdwatch-realtime-beta",
    },
  },
  {
    name: "production",
    branch: "main",
    plane: "production",
    hosts: {
      platform: "platform.birdwatch.com",
      webapp: "app.birdwatch.com",
      media: "media.birdwatch.com",
      realtime: "realtime.birdwatch.com",
    },
    vercelProjects: {
      platform: "birdwatch-platform",
      webapp: "birdwatch-webapp",
      media: "birdwatch-media",
      realtime: "birdwatch-realtime",
    },
  },
] as const satisfies readonly Environment[];

export type EnvironmentName = (typeof ENVIRONMENTS)[number]["name"];

export const ENVIRONMENT_NAMES: readonly EnvironmentName[] = ENVIRONMENTS.map(
  (environment) => environment.name,
);

export function isEnvironmentName(value: string): value is EnvironmentName {
  return ENVIRONMENT_NAMES.includes(value as EnvironmentName);
}

export function findEnvironment(name: string): Environment | undefined {
  return ENVIRONMENTS.find((environment) => environment.name === name);
}

/** Throws for a name the manifest does not list — callers pass a validated `EnvironmentName`. */
export function environment(name: EnvironmentName): Environment {
  const found = findEnvironment(name);
  if (!found) throw new Error(`Unknown environment "${name}"`);
  return found;
}
